/*
 * My Study notes as a file the reader keeps: plain text, because it opens on
 * any phone or computer and is the one format guaranteed not to touch what
 * they wrote. A note's own paragraph breaks, numbered lines, dashes and
 * indentation come out exactly as typed; only blank lines trailing off the
 * end of a note are dropped, so notes stay evenly spaced.
 *
 *   Compass Through The Bible - November 3, 2026
 *
 *   PEOPLE
 *
 *   David
 *   King of Israel • Era: United Monarchy
 *   Source: https://www.compassthroughthebible.com/people/david
 *
 *
 *   <the note, verbatim>
 *
 * A passage is headed by its Scripture reference alone, and its source is
 * the passage on Bible Gateway in the reader's chosen version.
 *
 * The building is plain data in, plain text out, so the mocha suite can check
 * it; only downloadText touches the page.
 */

const SECTIONS = [
  { type: "person", title: "People" },
  { type: "page", title: "Pages" },
  { type: "passage", title: "Passages" },
];

const RULE = "----------------------------------------";

/* The public site, so a source link works wherever the file is opened --
   not localhost when it was downloaded from a dev server. The same origin
   as scripts/lib/siteOrigin.js, which the build scripts use. */
export const SITE_ORIGIN = "https://www.compassthroughthebible.com";

/* Passages saved from the home page's "Read 2 Kings 4" link before it was
   labelled were stored under the link's screen-reader text. */
const LEGACY_PASSAGE_LABEL = /^Open the full chapter of (.+?) in your selected Bible version$/i;

/** A passage's Scripture reference, cleaned of that old label. */
export function passageReference(item) {
  const reference = String(item?.reference || "").trim();
  const legacy = LEGACY_PASSAGE_LABEL.exec(reference);
  return legacy ? legacy[1] : reference;
}

/** The title a note carries in the download. */
export function noteTitle(item) {
  return itemTitle(item);
}

/** The line under it in the download; a passage needs none. */
export function noteSubtitle(item) {
  return item?.type === "passage" ? "" : itemSubtitle(item);
}

/**
 * Where the note's subject can be found again: its page on the site, or for
 * a passage, the passage itself on Bible Gateway in the reader's version.
 */
export function noteSource(item, bibleVersion = "NIV") {
  if (item?.type === "person" && item.id) {
    return `${SITE_ORIGIN}/people/${encodeURIComponent(item.id)}`;
  }
  if (item?.type === "page" && item.path) {
    return `${SITE_ORIGIN}${item.path}`;
  }
  if (item?.type === "passage") {
    const reference = passageReference(item);
    if (!reference) return "";
    return `https://www.biblegateway.com/passage/?search=${encodeURIComponent(
      reference
    )}&version=${encodeURIComponent(bibleVersion)}`;
  }
  return "";
}

/**
 * What a saved item is called: "David", "Exodus Explorer", "Exodus 7:14-25".
 * A passage goes by its Scripture reference alone, even one saved from an
 * event window under the event's name.
 */
export function itemTitle(item) {
  if (item?.type === "passage") return passageReference(item);
  return item?.name || item?.title || item?.reference || "";
}

/**
 * The line under the title, as the notes panel shows it: a person's title
 * and era, or what kind of thing it is.
 */
export function itemSubtitle(item) {
  if (item?.type === "person") {
    /* "Era:" so "Judges" under Ruth reads as her time, not her book. */
    return [item.title, item.era ? `Era: ${item.era}` : ""]
      .filter(Boolean)
      .join(" • ");
  }
  if (item?.type === "passage") return "Scripture passage";
  return "Explorer page";
}

export function hasNote(item) {
  return Boolean(String(item?.note || "").trim());
}

/** "November 3, 2026" */
export function formatExportDate(date) {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function exportTitle(date) {
  return `Compass Through The Bible - ${formatExportDate(date)}`;
}

/* Line endings made consistent, and blank lines trailing off the end
   dropped. Nothing inside the note is touched. */
function noteBody(note) {
  return String(note || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\s+$/, "");
}

/**
 * The file's text, for the given items in the order given within each
 * section. Items without a note are left out: this is a file of notes.
 */
export function buildNotesText(items, date, { bibleVersion = "NIV" } = {}) {
  const title = exportTitle(date);
  const noted = (items || []).filter(hasNote);
  const blocks = [title, "=".repeat(title.length)];

  SECTIONS.forEach((section) => {
    const sectionItems = noted.filter((item) => item.type === section.type);
    if (!sectionItems.length) return;

    blocks.push("", "", section.title.toUpperCase(), RULE);

    sectionItems.forEach((item, index) => {
      if (index > 0) blocks.push("", RULE);
      blocks.push("", noteTitle(item));
      const subtitle = noteSubtitle(item);
      if (subtitle) blocks.push(subtitle);
      const source = noteSource(item, bibleVersion);
      if (source) blocks.push(`Source: ${source}`);
      /* Two blank lines, so the note stands clear of the heading above it. */
      blocks.push("", "", noteBody(item.note));
    });
  });

  return `${blocks.join("\n")}\n`;
}

/**
 * Hands the text to the browser as a download. The byte-order mark makes
 * Windows Notepad read it as UTF-8, so dashes and curly quotes survive.
 */
export function downloadText(fileName, text) {
  const blob = new Blob(["﻿", text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadNotes(items, { date = new Date(), bibleVersion } = {}) {
  downloadText(
    `${exportTitle(date)}.txt`,
    buildNotesText(items, date, { bibleVersion })
  );
}
